import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  SelectPicker,
  InputNumber,
  Table,
  IconButton,
} from "rsuite";
import { useTypedDispatch, useTypedSelector } from "App/Store";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { Category } from "core/models/Category";
import { getSeagmCategoryList } from "redux/action/seagmCategoryListAction";
import {
  addCategory,
  updateCategory,
  deleteCategory,
} from "core/services/Category";
import PromptModal from "shared/components/PromptModal";
import { useNotification } from "shared/hooks/useNotification";
import { DEFAULT_API_ERROR } from "core/constants/Defaults";
import EditIcon from "@rsuite/icons/legacy/Edit";
import TrashIcon from "@rsuite/icons/legacy/Trash";

const { Column, HeaderCell, Cell } = Table;

const GROUP_OPTIONS = [
  { label: "Gift Card", value: "giftcard" },
  { label: "Top Up", value: "topup" },
];

interface FormState {
  name: string;
  group: string | null;
  sortOrder: number;
}

const EMPTY_FORM: FormState = { name: "", group: null, sortOrder: 0 };

export default function SeagmCategories() {
  const dispatch = useTypedDispatch();
  const { isLoading, categories } = useTypedSelector(
    (state) => state.seagmCategories
  );
  const showNotification = useNotification();

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [formState, setFormState] = useState<FormState>(EMPTY_FORM);
  const [openDelete, setOpenDelete] = useState(false);
  const [selected, setSelected] = useState<Category | null>(null);

  function refresh() {
    dispatch(getSeagmCategoryList());
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isLoading) dispatch(showLoader());
    else dispatch(hideLoader());
  }, [isLoading, dispatch]);

  function openAdd() {
    setEditing(null);
    setFormState(EMPTY_FORM);
    setOpenForm(true);
  }

  function openEdit(category: Category) {
    setEditing(category);
    setFormState({
      name: category.name,
      group: category.group,
      sortOrder: category.sortOrder,
    });
    setOpenForm(true);
  }

  async function handleSave() {
    if (!formState.name.trim()) {
      showNotification({
        message: "Category name is required",
        title: "Error",
        type: "error",
      });
      return;
    }
    dispatch(showLoader());
    const payload = {
      name: formState.name.trim(),
      group: formState.group,
      sortOrder: formState.sortOrder,
    };
    const res = editing
      ? await updateCategory(payload, editing.id)
      : await addCategory(payload);
    if (res.status) {
      showNotification({
        message: editing ? "Category updated" : "Category created",
        title: "Success",
        type: "success",
      });
      setOpenForm(false);
      refresh();
    } else {
      showNotification({
        message: res.message || DEFAULT_API_ERROR,
        title: "Error",
        type: "error",
      });
    }
    dispatch(hideLoader());
  }

  async function handleDelete() {
    if (!selected) return;
    dispatch(showLoader());
    const res = await deleteCategory(selected.id);
    if (res.status) {
      showNotification({
        message: "Category deleted",
        title: "Success",
        type: "success",
      });
      refresh();
    } else {
      showNotification({
        message: res.message || DEFAULT_API_ERROR,
        title: "Error",
        type: "error",
      });
    }
    dispatch(hideLoader());
    setOpenDelete(false);
    setTimeout(() => setSelected(null), 300);
  }

  return (
    <>
      <div className="store-section-bar">
        <span />
        <Button appearance="primary" onClick={openAdd} className="btn">
          Add Category
        </Button>
      </div>

      <Table
        autoHeight
        data={categories}
        wordWrap
        rowHeight={56}
        headerHeight={48}
        className="seagm-category-table"
      >
        <Column flexGrow={2}>
          <HeaderCell className="cat-th">Name</HeaderCell>
          <Cell dataKey="name" className="cat-cell" />
        </Column>
        <Column flexGrow={1}>
          <HeaderCell className="cat-th">Group (Lens)</HeaderCell>
          <Cell className="cat-cell">
            {(row: Category) => (
              <span
                className={`seagm-cat-tag ${
                  row.group === "topup"
                    ? "is-topup"
                    : row.group === "giftcard"
                    ? "is-giftcard"
                    : "is-none"
                }`}
              >
                {row.group === "topup"
                  ? "Top Up"
                  : row.group === "giftcard"
                  ? "Gift Card"
                  : "None"}
              </span>
            )}
          </Cell>
        </Column>
        <Column flexGrow={1}>
          <HeaderCell className="cat-th">Slug</HeaderCell>
          <Cell dataKey="slug" className="cat-cell" />
        </Column>
        <Column width={90}>
          <HeaderCell className="cat-th">Order</HeaderCell>
          <Cell dataKey="sortOrder" className="cat-cell" />
        </Column>
        <Column width={130}>
          <HeaderCell className="cat-th">Actions</HeaderCell>
          <Cell className="cat-cell" style={{ padding: 8 }}>
            {(row: Category) => (
              <div style={{ display: "flex", gap: 8 }}>
                <IconButton
                  size="sm"
                  icon={<EditIcon />}
                  onClick={() => openEdit(row)}
                />
                <IconButton
                  size="sm"
                  color="red"
                  appearance="ghost"
                  icon={<TrashIcon />}
                  onClick={() => {
                    setSelected(row);
                    setOpenDelete(true);
                  }}
                />
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      <Modal open={openForm} onClose={() => setOpenForm(false)}>
        <Modal.Header>
          <Modal.Title>{editing ? "Edit Category" : "Add Category"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form fluid>
            <Form.Group>
              <Form.ControlLabel>Name</Form.ControlLabel>
              <Input
                value={formState.name}
                onChange={(value) =>
                  setFormState((s) => ({ ...s, name: value }))
                }
                placeholder="e.g. Gift Cards"
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Group (Lens)</Form.ControlLabel>
              <SelectPicker
                block
                cleanable
                data={GROUP_OPTIONS}
                value={formState.group}
                onChange={(value) =>
                  setFormState((s) => ({ ...s, group: value }))
                }
                placeholder="Select tab group"
              />
            </Form.Group>
            <Form.Group>
              <Form.ControlLabel>Sort Order</Form.ControlLabel>
              <InputNumber
                value={formState.sortOrder}
                onChange={(value) =>
                  setFormState((s) => ({ ...s, sortOrder: Number(value) || 0 }))
                }
                min={0}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={handleSave}>
            {editing ? "Update" : "Create"}
          </Button>
          <Button appearance="ghost" onClick={() => setOpenForm(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <PromptModal
        open={openDelete}
        onAccept={handleDelete}
        onReject={() => {
          setOpenDelete(false);
          setTimeout(() => setSelected(null), 300);
        }}
        acceptText="Delete"
        rejectText="Cancel"
        title="Delete Category"
        message={`Are you sure you want to delete ${
          selected?.name || "this category"
        }?`}
      />
    </>
  );
}
